/**
 * PRD.305 §8 — Patient Workspace → Labs.
 *
 * A patient-centric history of laboratory orders and results, reading the same
 * canonical rows the Laboratory module operates on. How much of it is usable is
 * decided by Laboratory Settings → Patient Workspace Access intersected with
 * RBAC (and with the Laboratory main-module toggle, which degrades this surface
 * to published-results-only rather than removing the patient's history).
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '@helios/auth-firebase-web';
import { qLabOrdersForPatient, qPatientById, type LabOrderRow } from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useLaboratoryCapabilities } from '../../hooks/useLaboratory';
import { useInvoiceModuleEnabled } from '../../hooks/useClinicMainModules';
import { PERM } from '../../auth/permissionMap';
import { LabsSurface } from '../laboratory/LabsSurface';

interface PatientRow {
  id: string;
  full_name: string | null;
  clinic_id?: string | null;
}

export function PatientLabsPage() {
  const { id: patientId = '' } = useParams();
  const { canAccess } = usePermissions();
  const capabilities = useLaboratoryCapabilities('patient_workspace');
  const invoiceEnabled = useInvoiceModuleEnabled();

  const { data: patients = [] } = usePowerSyncQuery<PatientRow>(qPatientById(patientId));
  const patient = patients[0] ?? null;

  const { data: orders = [], loaded } = usePowerSyncQuery<LabOrderRow>(
    qLabOrdersForPatient(patientId),
  );

  const orderContext = useMemo(
    () =>
      patient
        ? {
            patientId,
            patientName: patient.full_name ?? 'Patient',
            clinicId: patient.clinic_id ?? null,
          }
        : null,
    [patient, patientId],
  );

  // Billing is offered only when the clinic actually runs the Invoice module
  // and the user can bill — ordering itself never depends on either.
  const canBill = invoiceEnabled && canAccess(PERM.billing);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-6) 0',
          flexShrink: 0,
        }}
      >
        <h1
          style={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Labs
        </h1>
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            margin: '2px 0 0',
          }}
        >
          {capabilities.viewOperational
            ? 'Laboratory orders and results for this patient'
            : 'Published laboratory results for this patient'}
        </p>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 'var(--sp-5) var(--sp-6)' }}>
        <LabsSurface
          orders={orders}
          loaded={loaded}
          capabilities={capabilities}
          orderContext={orderContext}
          source="patient_workspace"
          canBill={canBill}
        />
      </div>
    </div>
  );
}

export default PatientLabsPage;
